<template>
  <v-app class="fill-height app-container">
    <AppSidebar />

    <v-main
      class="main-content"
      :style="{
        '--v-layout-left':
          shouldShowSidebar && !isMobile && !concentrationModeActive ? '56px' : '0px',
        'padding-top': reserveNavbarSpace ? '49px' : '0px',
      }"
    >
      <AppToast />
      <div ref="appBannersEl" class="app-banners" :style="{ top: appBannersTop }">
        <AppVersionUpdateBanner />
        <PwaInstallBanner />
        <EmailVerificationBanner />
        <ImpersonationBanner />
        <PremiumContestBanner />
        <DonationPromptBanner />
      </div>
      <div
        class="app-banners-spacer"
        :style="{ height: `${appBannersHeight}px` }"
        aria-hidden="true"
      />
      <OnlineGate v-if="routeRequiresOnline">
        <RouterView />
      </OnlineGate>
      <RouterView v-else />
      <HelpChat />
      <GroupChatsPanel />
      <NotificationsPreview />
      <ChapterSyncFloatingIndicator />
    </v-main>
    <header v-if="showDesktopNavbar" class="app-navbar">
      <AppNavbarActions />
    </header>
  </v-app>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue'
import { RouterView, useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useDisplay } from 'vuetify'
import { useAuthStore } from './stores/auth'
import { useUserProfileStore } from './stores/userProfile'
import { useConcentrationModeStore } from './stores/concentrationMode'
import { useNotificationsStore } from './stores/notifications'
import { useTheme } from './composables/useTheme'
import AppToast from './components/baseComponents/AppToast.vue'
import AppVersionUpdateBanner from './components/baseComponents/AppVersionUpdateBanner.vue'
import PwaInstallBanner from './components/baseComponents/PwaInstallBanner.vue'
import EmailVerificationBanner from './components/baseComponents/EmailVerificationBanner.vue'
import ImpersonationBanner from './components/admin/ImpersonationBanner.vue'
import PremiumContestBanner from './components/baseComponents/PremiumContestBanner.vue'
import DonationPromptBanner from './components/donations/DonationPromptBanner.vue'
import HelpChat from './components/help/HelpChat.vue'
import OnlineGate from './components/baseComponents/OnlineGate.vue'
import GroupChatsPanel from './components/community/GroupChatsPanel.vue'
import NotificationsPreview from './components/notifications/NotificationsPreview.vue'
import AppNavbarActions from './components/layout/AppNavbarActions.vue'
import AppSidebar from './components/layout/AppSidebar.vue'
import ChapterSyncFloatingIndicator from './components/sync/ChapterSyncFloatingIndicator.vue'
import { shouldShowAppSidebar } from './components/layout/appNavigation'
import { consumeLogoutReasonMessage } from './utils/logoutReason'
import { useToast } from './composables/useToast'
import { syncGrantedWebPushToken } from './composables/useNovuWebPush'
import { startWritingHabitsCollector } from './composables/useWritingHabitsCollector'
import BackupService from './services/backup'
import { bootChapterPersistenceOrchestrator } from './services/chapterPersistenceOrchestrator'

export default defineComponent({
  name: 'App',

  components: {
    RouterView,
    AppSidebar,
    ChapterSyncFloatingIndicator,
    AppToast,
    AppVersionUpdateBanner,
    PwaInstallBanner,
    EmailVerificationBanner,
    ImpersonationBanner,
    PremiumContestBanner,
    DonationPromptBanner,
    HelpChat,
    OnlineGate,
    GroupChatsPanel,
    NotificationsPreview,
    AppNavbarActions,
  },

  setup() {
    const theme = useTheme()
    const isMobile = useDisplay().mobile
    const concentrationModeStore = useConcentrationModeStore()
    const { active: concentrationModeActive } = storeToRefs(concentrationModeStore)
    const notificationsStore = useNotificationsStore()
    const { unreadCount: notificationsUnreadCount } = storeToRefs(notificationsStore)
    const route = useRoute()
    const routeRequiresOnline = computed(() => route.meta.requiresOnline === true)

    return {
      theme,
      isMobile,
      concentrationModeActive,
      notificationsStore,
      notificationsUnreadCount,
      routeRequiresOnline,
    }
  },

  data() {
    return {
      authStore: useAuthStore(),
      userProfileStore: useUserProfileStore(),
      appBannersHeight: 0,
      appBannersObserver: null as ResizeObserver | null,
    }
  },

  computed: {
    reserveNavbarSpace(): boolean {
      return (
        (this.isMobile && this.shouldShowSidebar) ||
        this.showDesktopNavbar
      )
    },

    showDesktopNavbar(): boolean {
      return (
        !this.isMobile &&
        this.shouldShowSidebar &&
        !this.concentrationModeActive &&
        this.$route.name !== 'project'
      )
    },

    appBannersTop(): string {
      if (this.reserveNavbarSpace) {
        return '49px'
      }
      return '0px'
    },

    shouldShowSidebar(): boolean {
      return shouldShowAppSidebar(
        this.authStore.userType,
        this.$route.name,
        this.$route.meta.publicMarketing,
      )
    },
  },

  mounted() {
    BackupService.initialize()
    bootChapterPersistenceOrchestrator()
    this.showLogoutReasonToast()
    this.loadUserProfile()
    void this.authStore.fetchSessionInfo()
    this.syncWebPushTokenIfGranted()
    this.bindAppBannersObserver()
    startWritingHabitsCollector()
  },

  beforeUnmount() {
    this.unbindAppBannersObserver()
  },

  methods: {
    bindAppBannersObserver() {
      const el = this.$refs.appBannersEl as HTMLElement | undefined
      if (!el || typeof ResizeObserver === 'undefined') {
        return
      }

      this.appBannersObserver = new ResizeObserver(() => {
        this.appBannersHeight = el.offsetHeight
      })
      this.appBannersObserver.observe(el)
      this.appBannersHeight = el.offsetHeight
    },

    unbindAppBannersObserver() {
      this.appBannersObserver?.disconnect()
      this.appBannersObserver = null
    },

    showLogoutReasonToast() {
      const message = consumeLogoutReasonMessage()
      if (!message) return
      const { triggerToast } = useToast()
      triggerToast({
        message,
        color: 'warning',
        timeout: 8000,
      })
    },

    async loadUserProfile() {
      const userId = this.authStore.currentUser?.userId
      if (userId && this.authStore.userType === 'registered') {
        await this.userProfileStore.fetchProfile(userId)
      }
    },

    syncWebPushTokenIfGranted() {
      if (this.authStore.userType !== 'registered' || this.authStore.isImpersonating) {
        return
      }
      void syncGrantedWebPushToken()
    },
  },

  watch: {
    shouldShowSidebar: {
      immediate: true,
      handler(show: boolean) {
        if (show && this.authStore.userType === 'registered') {
          void this.notificationsStore.init()
        } else {
          this.notificationsStore.reset()
        }
      },
    },
    'authStore.accessToken'() {
      this.loadUserProfile()
      this.syncWebPushTokenIfGranted()
      this.notificationsStore.reset()
      if (this.shouldShowSidebar && this.authStore.userType === 'registered') {
        void this.notificationsStore.init()
      }
    },
  },
})
</script>

<style scoped>
:deep(.v-main) {
  margin-left: 0 !important;
  left: 0 !important;
}

.main-content {
  margin-left: 0 !important;
  left: 0 !important;
  transition: margin-left 0.2s ease;
}

.app-banners {
  position: fixed;
  left: 0;
  right: 0;
  z-index: 1;
  width: 100%;
  background: rgb(var(--v-theme-surface));
}

.app-banners-spacer {
  width: 100%;
  flex-shrink: 0;
  pointer-events: none;
}

.app-navbar {
  position: fixed;
  top: 0;
  right: 0;
  left: 56px;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 49px;
  padding-right: 8px;
  background: rgb(var(--v-theme-primary));
}
</style>
